







// @ts-nocheck
// ^^^^^^^^^^^ Disabled TypeScript linter in this file because it doesn't understand Vue components
// which is not wrapped in Vue.extend(), and we can't use Vue.extend() in Sanity serializers
// because of how src/components/SanityBlockContent.vue initializes the serializers.

import marked from 'marked';
import 'highlight.js/styles/atom-one-dark.css';
import hljs from 'highlight.js';
import { getTitleForLanguage } from '@/utils/codeSyntaxSupport';

export default {
  name: 'SanityMarkdownContent',
  props: {
    markdown: String,
  },
  data() {
    return {
      language: 'plaintext',
      code: '',
    };
  },
  methods: {
    // Turns an object into a HTML style string
    // Example: style="display: block; margin: 8px;"
    stringifyStyles(styles) {
      return Object.keys(styles).reduce((all, cur) => {
        const val = styles[cur];
        return `${all};${cur}:${val}`;
      }, '');
    },

    // Mirrors the .root class in ./SanityCodeBlockSerializer.vue.
    // Any changes here should be mirrored there and vice versa.
    rootStyles() {
      const styles = {
        display: 'flex',
        'flex-direction': 'column',
        'margin-bottom': '16px',
        'max-width': '1000px',
      };
      return this.stringifyStyles(styles);
    },

    // Mirrors the .file class in ./SanityCodeBlockSerializer.vue.
    // Any changes here should be mirrored there and vice versa.
    fileStyles() {
      const styles = {
        width: 'fit-content',
        'min-width': '150px',
        'max-width': '500px',
        height: '50px',
        'background-color': '#282c34',
        'border-top-left-radius': '8px',
        'border-top-right-radius': '8px',
        display: 'inline-flex',
        'justify-content': 'center',
        'align-items': 'center',
      };
      return this.stringifyStyles(styles);
    },

    // Mirrors the .filename class in ./SanityCodeBlockSerializer.vue.
    // Any changes here should be mirrored there and vice versa.
    filenameStyles() {
      const styles = {
        'margin-right': '8px',
        'font-weight': '600',
      };
      return this.stringifyStyles(styles);
    },

    // Mirrors the .highlight class in ./SanityCodeBlockSerializer.vue.
    // Any changes here should be mirrored there and vice versa.
    highlightStyles() {
      const styles = {
        'max-width': '960px',
        margin: '0',
        display: 'inline-flex',
      };
      return this.stringifyStyles(styles);
    },

    // Mirrors the .code class in ./SanityCodeBlockSerializer.vue.
    // Any changes here should be mirrored there and vice versa.
    codeStyles({ language }) {
      const styles = {
        'font-size': '14pt',
        'font-weight': '400',
        'border-top-left-radius': language ? '0' : '8px',
        'border-top-right-radius': '8px',
        'border-bottom-right-radius': '8px',
        'border-bottom-left-radius': '8px',
        width: '100%',
        overflow: 'auto',
      };
      return this.stringifyStyles(styles);
    },
  },
  mounted() {
    const renderer = new marked.Renderer();
    renderer.code = (code, language) => {
      // This HTML and styling mirrors ./SanityCodeBlockSerializer.vue and its HTML layout and styling 1:1.
      // Due to the way marked's renderer processes the HTML returned from this function (seemingly) outside of Vue, I found no way of re-using the existing layout
      // and styling in SanityCodeBlockSerializer and chose to re-make the same layout here using the more convoluted and messy approach of pure javascript.
      // I've tried using the exact same names for things to make it easier to find and correlate things across the two components.
      // Any changes here should be mirrored in SanityCodeBlockSerializer and vice versa.

      const root = (file, pre) => `<div id="root" style=${this.rootStyles()}>${file}${pre}</div>`;
      const file = (children) => (language ? `<div id="file" style=${this.fileStyles()}>${children}</div>` : '');
      const filename = () => `<span id="filename" style=${this.filenameStyles()}>${getTitleForLanguage(language)}</span>`;
      const pre = (children) => `<pre id="pre" style=${this.highlightStyles()}>${children}</pre>`;
      const codeTag = () => `<code style=${this.codeStyles({ language })} class="language-${language} hljs">${code}</code>`;

      return root(
        file(filename()),
        pre(codeTag()),
      );
    };

    // https://marked.js.org/using_advanced#options
    marked.setOptions({
      renderer,
      // If true, conform to the original markdown.pl as much as possible. Don't fix original markdown bugs or behavior. Turns off and overrides gfm.
      pedantic: false,
      // If true, use approved GitHub Flavored Markdown (GFM) specification.
      gfm: true,
      // If true, add <br> on a single line break (copies GitHub behavior on comments, but not on rendered markdown files). Requires gfm be true.
      breaks: false,
      // If true, sanitize the HTML passed into markdownString with the sanitizer function.
      sanitize: false,
      // If true, use smarter list behavior than those found in markdown.pl.
      smartLists: true,
      // If true, use "smart" typographic punctuation for things like quotes and dashes.
      smartypants: false,
      // If true, emit self-closing HTML tags for void elements (<br/>, <img/>, etc.) with a "/" as required by XHTML.
      xhtml: false,
    });
    this.code = marked(this.markdown);
  },
  updated() {
    // https://www.metachris.com/2017/02/vuejs-syntax-highlighting-with-highlightjs/#a-custom-vuejs-directive-v-highlightjs

    // Look for all <code /> tags in the DOM which we want to highlight
    const targets = document.querySelectorAll('code');
    targets.forEach((target) => {
      // There are two kinds of code in markdown: A `inline` variant and a
      // ```
      // multiline
      // ```
      // variant. We want to highlight only the multiline variant and leave styling of the inline variant to the site's global CSS styling.
      //
      // The difference in HTML between the two is that multiline code blocks has a <pre> tag surrounding the <code /> tag,
      // while the inline variant only has a <code /> element with no <pre> parent.
      //
      // To only highlight multiline code blocks, we therefore have to look for a <pre> tag parent element and only style those.
      if (target.parentElement?.tagName === 'PRE') {
        hljs.highlightBlock(target);
      }
    });
  },
};
